import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { CategoryBento } from "../components/home/CategoryBento";
import { ProductCard } from "../components/site/ProductCard";
import { SectionHead } from "../components/site/Motion";
import { api, useCategories } from "../lib/api";
import { Seo, crumbsLd } from "../components/site/Seo";

export default function Products() {
  const { categories } = useCategories();
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["products"], queryFn: () => api.get("/products").then((r) => r.data) });
  const products = Array.isArray(data) ? data : [];
  const slugOf = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.slug])), [categories]);
  const list = products.filter((p) => (cat === "all" || p.category_id === cat) && p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="products-page">
      <Seo path="/products" title="Rubber Products Catalogue: Grommets, Seals, O-Rings | Autocrat" description="Browse molded rubber parts, extruded profiles, grommets, bushes, gaskets, seals, O-rings and custom components made by Autocrat Rubber Industries, Gurugram." jsonLd={crumbsLd([["Home", "/"], ["Products", "/products"]])} />
      <PageHero testId="products-hero" video eyebrow="Product catalogue" image="/media/cat_orings.jpg" lines={["The product", { text: "index.", className: "text-outline" }]} crumbs={[{ to: "/", label: "Home" }, { label: "Products" }]}>
        Browse by family or search the full parts index. Every part can be developed to your drawing, sample or compound requirement.
      </PageHero>
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="container-x">
          <SectionHead eyebrow="Browse by category" title="Product families" />
          <div className="mt-12"><CategoryBento categories={categories} /></div>
        </div>
      </section>
      <section className="bg-white py-20 md:py-28">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <SectionHead eyebrow="All parts" title="Search the index" />
            <label className="flex w-full items-center gap-3 border-b-2 border-slate-900 pb-2 lg:w-96">
              <Search className="h-5 w-5 text-slate-500" />
              <input data-testid="product-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search parts e.g. grommet" className="w-full bg-transparent text-base outline-none" />
            </label>
          </div>
          <div className="mt-10 flex flex-wrap gap-2" data-testid="category-filter-bar">
            {[{ id: "all", name: "All" }, ...categories].map((c) => (
              <button
                key={c.id}
                data-testid={`category-filter-button-${c.slug || "all"}`}
                onClick={() => setCat(c.id)}
                className={`border px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-[background-color,color,border-color] duration-200 ${
                  cat === c.id ? "border-[#0a6cff] bg-[#0a6cff] text-white" : "border-slate-300 text-slate-700 hover:border-slate-900"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((p, i) => <ProductCard key={p.id} p={p} i={i} catSlug={slugOf[p.category_id] || "all"} />)}
          </div>
          {list.length === 0 && <p data-testid="products-empty" className="mt-10 text-sm text-slate-500">No parts match your search. Try another term or send us an RFQ.</p>}
        </div>
      </section>
    </div>
  );
}
